import { useDrag } from "@norkron/web-dnd";
import { Link } from "react-router-dom";
import type { CommentStatus, Ticket } from "../../api/types";
import { AlertTriangle, GripVertical, Link2 } from "../../compose/Icon";
import { initials } from "../../lib/format";
import { WRITABLE_STATUSES } from "../../lib/kanban";
import {
  anchorIsFlagged,
  anchorStateLabel,
  formatAge,
  statusLabel,
  typeLabel,
} from "../../lib/tickets";
import { ticketTypeIcon } from "../../lib/ticketIcons";
import { buildCitationPath, CITATION_PARAM } from "../../lib/permalink";

/**
 * A kanban card = one ticket (design tickets-kanban §4.2). It is the DnD drag
 * source (`useDrag` from `@norkron/web-dnd` — RULE ZERO, no hand-rolled drag).
 * The card body is the drag handle; a dedicated title `<Link>` navigates to the
 * same target the list row uses (so a drag never fires navigation), and a native
 * "Move to…" `<select>` is the WCAG non-drag equivalent (§4.6) that drives the
 * SAME `onMove` — also the deterministic path when HTML5 DnD is flaky under test.
 * All chrome (type/anchor badges, avatar, status stripe) reuses the existing
 * tickets classes + CSS tokens; nothing is re-styled with raw values.
 */

/** The row's deep-link target: unit-scoped tickets drill into the workbench pane,
 *  loose/legacy docs keep the document-canvas link — both carry the CANONICAL
 *  citation param `?comment=<id>` (the copy-link permalink scheme). Mirrors
 *  `Tickets.tsx TicketRow`. */
function ticketHref(slug: string, ticket: Ticket): string {
  const role = ticket.resource_role || "article";
  return ticket.article_unit_id != null
    ? `/w/${slug}/article/${ticket.article_unit_id}?show=${role}&${CITATION_PARAM}=${ticket.id}`
    : buildCitationPath(slug, String(ticket.document_id), ticket.id);
}

export function KanbanCard({
  ticket,
  slug,
  onMove,
}: {
  ticket: Ticket;
  slug: string;
  onMove: (ticketId: number, status: CommentStatus) => void;
}) {
  const drag = useDrag({ id: String(ticket.id), itemType: "ticket" });
  // Struck through when the quote is NOT in the document — which is true of a
  // comment that lost its anchor AND of one that never had it.
  const orphaned =
    ticket.anchor_state === "orphaned" || ticket.anchor_state === "unanchored";

  return (
    <article
      {...drag.bind}
      className={`kanban-card kanban-card--${ticket.status}${
        drag.isDragging ? " kanban-card--dragging" : ""
      }`}
      data-testid={`kanban-card-${ticket.id}`}
    >
      <div className="kanban-card__top">
        <span
          className={`badge badge--type-${ticket.type}`}
          data-testid={`kanban-card-type-${ticket.id}`}
        >
          <span className="tRow__typeicon" aria-hidden>
            {ticketTypeIcon(ticket.type)}
          </span>
          {typeLabel(ticket.type)}
        </span>
        <span
          className="kanban-card__handle"
          data-testid={`kanban-card-handle-${ticket.id}`}
          aria-hidden
        >
          <GripVertical size={14} strokeWidth={1.5} />
        </span>
      </div>

      <Link
        to={ticketHref(slug, ticket)}
        className="kanban-card__title"
        data-testid={`kanban-card-open-${ticket.id}`}
        draggable={false}
      >
        <span
          className={`kanban-card__quote${
            orphaned ? " tRow__quote--orphaned" : ""
          }`}
        >
          {ticket.quote || ticket.body}
        </span>
      </Link>

      {anchorIsFlagged(ticket.anchor_state) ? (
        <span
          className={`badge badge--anchor-${ticket.anchor_state}`}
          data-testid={`kanban-card-anchor-${ticket.id}`}
        >
          {/* Not-in-the-document (orphaned | unanchored) → broken link;
              moved-but-present (shifted | repaired) → warning. */}
          {ticket.anchor_state === "orphaned" ||
          ticket.anchor_state === "unanchored" ? (
            <Link2 size={13} strokeWidth={1.5} aria-hidden />
          ) : (
            <AlertTriangle size={13} strokeWidth={1.5} aria-hidden />
          )}
          {anchorStateLabel(ticket.anchor_state)}
        </span>
      ) : null}

      <div className="kanban-card__foot">
        <span className="kanban-card__who">
          <span className="avatar avatar--sm" aria-hidden>
            {initials(ticket.author_name)}
          </span>
          <span className="kanban-card__whoname">{ticket.author_name}</span>
        </span>
        <span className="kanban-card__age" title={ticket.created_at}>
          {formatAge(ticket.created_at)}
        </span>
      </div>

      {/* WCAG non-drag equivalent (design §4.6) — the 4 writable statuses only. */}
      <select
        className="kanban-card__move"
        data-testid={`kanban-card-move-${ticket.id}`}
        aria-label={`Move ticket ${ticket.id} to a status`}
        value=""
        onChange={(e) => {
          const next = e.target.value as CommentStatus | "";
          if (next) onMove(ticket.id, next);
        }}
      >
        <option value="">Move to…</option>
        {WRITABLE_STATUSES.map((s) => (
          <option key={s} value={s}>
            {statusLabel(s)}
          </option>
        ))}
      </select>
    </article>
  );
}
